import React, { ReactNode } from 'react';
import * as Yup from 'yup';
import { Input, Select } from 'formik-antd';
import { Col, Row } from 'antd';
import { has, isEmpty } from 'lodash';

import { getFormItemName } from 'utils/fieldArrayUtils';
import FormItem from 'components/formik/FormItem';
import { getPayRateTypeOptions } from 'utils/assignmentUtils';
import InputNumber from 'components/formik/InputNumber';
import BaseIndicatorNumber from 'components/wrappers/BaseIndicatorNumber';
import { MultiRateSchedulerEditModel } from 'api/backofficeApiClient';
import { FinancialSchedulerFormValues } from 'components/modals/FinancialInfoSchedulerModal';

import styles from '../../../FinancialInfo.module.less';

type Props = {
  index: number;
  formNamePrefix: string;
  actionButton: ReactNode;
  renderChangesEffectiveDateBtn: () => React.ReactNode;
  predefinedMultiRates?: MultiRateSchedulerEditModel[];
};

const requiredMessage = 'Field is required';
const maxCharactersLength = 255;

export const initialMultipleRatesValidationSchema = {
  payType: Yup.number().nullable().required(requiredMessage),
  payRate: Yup.number()
    .nullable()
    .min(0, 'Pay rate should be from 0 to 999999')
    .max(999999, 'Pay rate should be from 0 to 999999'),
  billRate: Yup.number()
    .nullable()
    .min(0, 'Bill rate should be from 0 to 999999')
    .max(999999, 'Bill rate should be from 0 to 999999'),
  specialInstuctions: Yup.string()
    .trim()
    .max(
      maxCharactersLength,
      `Special instructions should be ${maxCharactersLength} characters maximum`,
    ),
};

export const submitMultipleRatesValidationSchema = (
  predefinedFinancialSchedulerValues?: FinancialSchedulerFormValues,
) => {
  return {
    ...initialMultipleRatesValidationSchema,
    payRate: Yup.lazy((_, options) => {
      const index = (options as any)?.index;
      let schema: any = initialMultipleRatesValidationSchema.payRate;

      const correctScheduler = (
        predefinedFinancialSchedulerValues?.multiRateSchedulers || []
      ).find(
        (multiRate) =>
          multiRate.index === index && multiRate.hasOwnProperty('payRate'),
      );

      if (isEmpty(correctScheduler)) {
        schema =
          initialMultipleRatesValidationSchema.payRate.required(
            requiredMessage,
          );
      }

      return schema;
    }),
    billRate: Yup.lazy((_, options) => {
      const index = (options as any)?.index;
      let schema: any = initialMultipleRatesValidationSchema.billRate;

      const correctScheduler = (
        predefinedFinancialSchedulerValues?.multiRateSchedulers || []
      ).find(
        (multiRate) =>
          multiRate.index === index && multiRate.hasOwnProperty('billRate'),
      );

      if (isEmpty(correctScheduler)) {
        schema =
          initialMultipleRatesValidationSchema.billRate.required(
            requiredMessage,
          );
      }

      return schema;
    }),
  };
};

const isDisabledField = (
  name: string,
  index: number,
  multiRates?: MultiRateSchedulerEditModel[],
) => {
  const scheduledChanged = (multiRates || []).find(
    (multiRate) => multiRate.index === index && has(multiRate, name),
  );
  return Boolean(scheduledChanged);
};

export default function FinancialRatesSection(props: Props) {
  const {
    formNamePrefix,
    index,
    actionButton,
    renderChangesEffectiveDateBtn,
    predefinedMultiRates,
  } = props;

  const payRateTypeFormItemName = getFormItemName({
    name: 'payType',
    index,
    prefix: formNamePrefix,
  });
  const payRateFormItemName = getFormItemName({
    name: 'payRate',
    index,
    prefix: formNamePrefix,
  });
  const billRateFormItemName = getFormItemName({
    name: 'billRate',
    index,
    prefix: formNamePrefix,
  });
  const specialInstructionsFormItemName = getFormItemName({
    name: 'specialInstuctions',
    index,
    prefix: formNamePrefix,
  });

  return (
    <>
      <Row gutter={40}>
        <Col xs={24} md={12}>
          <FormItem
            name={payRateTypeFormItemName}
            label={BaseIndicatorNumber({
              number: index + 2,
              title: 'Pay rate type *',
            })}
          >
            <Select
              name={payRateTypeFormItemName}
              placeholder="Select rate type"
            >
              {getPayRateTypeOptions().map((payRateType) => (
                <Select.Option
                  key={`select-payRateType-${payRateType.value}`}
                  value={payRateType.value}
                >
                  <div className={styles.selectTitle}>
                    {payRateType.label?.title} - {''}
                    <span>{payRateType.label?.subTitle}</span>
                  </div>
                  <div className={styles.selectText}>
                    {payRateType.label?.description}
                  </div>
                </Select.Option>
              ))}
            </Select>
          </FormItem>
        </Col>
        <Col xs={24} md={12}>
          <div className={styles.addRateBtn}>{actionButton}</div>
        </Col>
        <Col xs={24} md={12}>
          <FormItem
            name={payRateFormItemName}
            label={BaseIndicatorNumber({
              number: index + 2,
              title: 'Pay rate *',
            })}
          >
            <InputNumber
              name={payRateFormItemName}
              placeholder="0.00"
              disabled={isDisabledField('payRate', index, predefinedMultiRates)}
              className="full-width"
              precision={2}
              inputPrefix={'$'}
            />
          </FormItem>
        </Col>
        <Col xs={24} md={12}>
          <FormItem
            name={billRateFormItemName}
            label={BaseIndicatorNumber({
              number: index + 2,
              title: 'Bill rate *',
            })}
          >
            <InputNumber
              name={billRateFormItemName}
              placeholder="0.00"
              disabled={isDisabledField(
                'billRate',
                index,
                predefinedMultiRates,
              )}
              className="full-width"
              precision={2}
              inputPrefix={'$'}
            />
          </FormItem>
        </Col>
        <Col xs={24} md={12}>
          <FormItem
            name={specialInstructionsFormItemName}
            label="Special instructions"
          >
            <Input.TextArea
              name={specialInstructionsFormItemName}
              placeholder="Enter special instructions"
            />
          </FormItem>
        </Col>
        {renderChangesEffectiveDateBtn()}
      </Row>
    </>
  );
}
